import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeftRight, GitMerge } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { createDeviceMerge } from '@/lib/reseller-api';
import type { Device } from '@/lib/traccar-api';

interface MergeDevicesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  devices: [Device, Device];
  onSuccess: () => void;
}

export function MergeDevicesDialog({ open, onOpenChange, devices, onSuccess }: MergeDevicesDialogProps) {
  const { t } = useTranslation();
  const [primaryIndex, setPrimaryIndex] = useState<0 | 1>(0);
  const [displayName, setDisplayName] = useState('');

  const primary = devices[primaryIndex];
  const secondary = devices[primaryIndex === 0 ? 1 : 0];

  const mutation = useMutation({
    mutationFn: () => createDeviceMerge({
      primary_device_id: primary.id,
      secondary_device_id: secondary.id,
      display_name: displayName.trim() || undefined,
    }),
    onSuccess: () => {
      toast.success(t('devices.mergeSuccess'));
      onOpenChange(false);
      onSuccess();
    },
    onError: () => {
      toast.error(t('devices.mergeError'));
    },
  });

  const swap = () => setPrimaryIndex(i => i === 0 ? 1 : 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-5 w-5" />
            {t('devices.mergeDevices')}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">{t('devices.mergeDescription')}</p>

        <div className="space-y-3">
          <div className="rounded-lg border p-3 space-y-1">
            <div className="flex items-center justify-between">
              <Badge variant="default" className="text-xs">{t('devices.primaryDevice')}</Badge>
            </div>
            <p className="font-medium">{primary.name}</p>
            <p className="text-xs text-muted-foreground font-mono">{primary.uniqueId}</p>
          </div>

          <div className="flex justify-center">
            <Button variant="outline" size="sm" onClick={swap} className="gap-1.5">
              <ArrowLeftRight className="h-3.5 w-3.5" />
              {t('devices.swapDevices')}
            </Button>
          </div>

          <div className="rounded-lg border p-3 space-y-1 opacity-70">
            <div className="flex items-center justify-between">
              <Badge variant="secondary" className="text-xs">{t('devices.secondaryDevice')}</Badge>
            </div>
            <p className="font-medium">{secondary.name}</p>
            <p className="text-xs text-muted-foreground font-mono">{secondary.uniqueId}</p>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="display-name">{t('devices.mergeDisplayName')}</Label>
          <Input
            id="display-name"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            placeholder={t('devices.mergeDisplayNamePlaceholder')}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            <GitMerge className="h-4 w-4 mr-1.5" />
            {t('devices.merge')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
